import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { AppProvider, useApp } from './context/AppContext'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Invoices from './pages/Invoices'
import InvoiceEditor from './pages/InvoiceEditor'
import InvoiceView from './pages/InvoiceView'
import Clients from './pages/Clients'
import ClientPortal from './pages/ClientPortal'
import ReceiptPortal from './pages/ReceiptPortal'
import PublicClientDashboard from './pages/PublicClientDashboard'
import Settings from './pages/Settings'
import Reports from './pages/Reports'
import Auth from './pages/Auth'
import Loading from './pages/Loading'
import Landing from './pages/Landing'
import Profile from './pages/Profile'
import ClientView from './pages/ClientView'
import TermsOfService from './pages/TermsOfService'
import PrivacyPolicy from './pages/PrivacyPolicy'

// Protected Route Wrapper
function ProtectedRoute() {
  const { user, loading } = useApp()

  if (loading) {
    return <Loading />
  }

  // Allow access if user is logged in
  if (user) {
    return <Outlet />
  }

  // Redirect to auth if not logged in
  return <Navigate to="/auth" replace />
}

// Public Route (redirect to dashboard if already logged in)
function PublicRoute() {
  const { user, loading } = useApp()

  if (loading) {
    return <Loading />
  }

  // Redirect to dashboard if already logged in
  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return <Auth />
}

// Landing Route (public, shows landing page or redirects to dashboard if logged in)
function LandingRoute() {
  const { user, loading } = useApp()

  if (loading) {
    return <Loading />
  }

  // Redirect to dashboard if already logged in
  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return <Landing />
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Routes>
          {/* Landing Page (Public) */}
          <Route path="/" element={<LandingRoute />} />

          {/* Auth Route (Public) */}
          <Route path="/auth" element={<PublicRoute />} />

          {/* Client Portal Routes (Public) */}
          <Route path="/portal/:invoiceId" element={<ClientPortal />} />
          <Route path="/portal/:invoiceId/receipt" element={<ReceiptPortal />} />
          <Route path="/portal/client/:clientId" element={<PublicClientDashboard />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Admin Routes (Protected) */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/new" element={<InvoiceEditor />} />
              <Route path="invoices/:id/edit" element={<InvoiceEditor />} />
              <Route path="invoices/:id/view" element={<InvoiceView />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:id" element={<ClientView />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Route>

          {/* Catch all - redirect to landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AppProvider>
  )
}

export default App
